import { memo, useMemo } from 'react';
import { Box, Button, Chip, IconButton, Tooltip, Typography } from '@mui/material';
import BarChartIcon from '@mui/icons-material/BarChart';
import CloseIcon from '@mui/icons-material/Close';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import RemoveRedEyeIcon from '@mui/icons-material/RemoveRedEye';
import { Notification } from '../../../../../services/NotificationService';
import { SEVERITY_BG, SEVERITY_COLOR } from './constants';

interface ParsedCountMessage {
  message?: string;
  listCount?: Array<{ channel: string; count: number }> | null;
}

function parseMessage(raw: string): ParsedCountMessage | null {
  if (!raw?.trim().startsWith('{')) return null;
  try { return JSON.parse(raw) as ParsedCountMessage; }
  catch { return null; }
}

export interface NotificationItemProps {
  notification: Notification;
  read: boolean;
  onMarkAsRead: (id: string) => void;
  onArchive: (id: string) => void;
  onOpenDialog: (queryId: string) => void;
}

export const NotificationItem = memo(function NotificationItem({
  notification,
  read,
  onMarkAsRead,
  onArchive,
  onOpenDialog,
}: NotificationItemProps) {
  const isCount = (notification.type || '').toUpperCase() === 'COUNT';

  const parsed = useMemo(
    () => parseMessage(notification.message),
    [notification.message],
  );

  const displayMsg = parsed?.message || notification.message;
  const color = SEVERITY_COLOR[notification.severity] || SEVERITY_COLOR.info;
  const bg    = SEVERITY_BG[notification.severity]   || SEVERITY_BG.info;

  return (
    <Box
      data-notification-id={notification.id}
      sx={{
        mb: 1.5,
        borderRadius: 2,
        border: `1px solid ${color}40`,
        borderLeft: `4px solid ${color}`,
        bgcolor: read ? '#fafafa' : bg,
        opacity: read ? 0.75 : 1,
        transition: 'opacity 0.2s',
        overflow: 'hidden',
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', px: 1.5, pt: 1.2, pb: 0.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
          {isCount && <BarChartIcon sx={{ fontSize: 16, color }} />}
          <Typography variant="subtitle2" sx={{ fontWeight: 700, fontSize: '0.82rem', color }}>
            {notification.type}
          </Typography>
          {!read && (
            <Chip
              label="Nouveau"
              size="small"
              sx={{
                height: 16,
                fontSize: '0.65rem',
                fontWeight: 700,
                bgcolor: color,
                color: '#fff',
                '& .MuiChip-label': { px: 0.8 },
              }}
            />
          )}
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Tooltip title={read ? 'Marquer comme non lu' : 'Marquer comme lu'}>
            <IconButton
              size="small"
              onClick={() => onMarkAsRead(notification.id)}
              sx={{ color: read ? 'text.disabled' : color, p: 0.4 }}
            >
              {read
                ? <VisibilityOffIcon sx={{ fontSize: 15 }} />
                : <RemoveRedEyeIcon  sx={{ fontSize: 15 }} />}
            </IconButton>
          </Tooltip>
          <Tooltip title="Archiver">
            <IconButton
              size="small"
              onClick={() => onArchive(notification.id)}
              sx={{ p: 0.4, color: 'text.secondary' }}
            >
              <CloseIcon sx={{ fontSize: 15 }} />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      <Box sx={{ px: 1.5, pb: parsed?.listCount ? 0.5 : 1 }}>
        <Typography
          variant="body2"
          sx={{ fontSize: '0.8rem', fontWeight: read ? 400 : 500, color: 'text.primary', lineHeight: 1.4 }}
        >
          {displayMsg}
        </Typography>
      </Box>

      {parsed?.listCount && parsed.listCount.length > 0 && (
        <Box sx={{ px: 1.5, pb: 0.8, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
          {parsed.listCount.map((c) => (
            <Chip
              key={c.channel}
              label={`${c.channel} : ${c.count.toLocaleString('fr-FR')}`}
              size="small"
              sx={{
                fontSize: '0.7rem',
                height: 20,
                bgcolor: `${color}15`,
                color,
                border: `1px solid ${color}40`,
                '& .MuiChip-label': { px: 1 },
              }}
            />
          ))}
        </Box>
      )}

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', px: 1.5, pb: 1 }}>
        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.68rem' }}>
          {notification.queryId && (
            <span style={{ marginRight: 6 }}>Requête #{notification.queryId} ·</span>
          )}
          {new Date(notification.timestamp).toLocaleString('fr-FR', {
            day: '2-digit',
            month: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            timeZone: 'Europe/Paris',
          })}
        </Typography>

        {isCount && notification.queryId && (
          <Button
            size="small"
            startIcon={<BarChartIcon sx={{ fontSize: '13px !important' }} />}
            onClick={() => onOpenDialog(notification.queryId!)}
            sx={{
              fontSize: '0.7rem',
              py: 0.3, px: 1,
              minHeight: 0,
              color,
              borderColor: `${color}60`,
              border: '1px solid',
              borderRadius: 1.5,
              textTransform: 'none',
              lineHeight: 1.4,
            }}
          >
            Voir les résultats
          </Button>
        )}
      </Box>
    </Box>
  );
});
